// @ts-nocheck
import React, { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';

interface SortOption {
	columnName: string;
	displayName: string;
}

interface KitSelectionBoxProps {
	kitList: any[];
	kitIndex: number;
	onNavigate: (direction: number) => void;
	onSelectIndex: (index: number) => void;
	loading: boolean;
	availInfo?: {
		initialAvail: number;
		currentAvail: number;
		orderedQty: number;
		remainQty: number;
	};
	/** Available kit-level columns to sort by */
	sortOptions?: SortOption[];
	/** Currently active sort field (empty = default/discovery order) */
	sortField?: string;
	/** Callback when user changes the sort field */
	onSortChange?: (field: string) => void;
}

const KitSelectionBox: React.FC<KitSelectionBoxProps> = ({
	kitList,
	kitIndex,
	onNavigate,
	onSelectIndex,
	loading,
	availInfo,
	sortOptions = [],
	sortField = '',
	onSortChange,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const [kitSearch, setKitSearch] = useState('');
	const [dropOpen, setDropOpen] = useState(false);
	const dropRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const currentKit = kitList[kitIndex] || null;

	const fieldNames = useMemo(() => {
		if (!currentKit) return {};
		const keys = Object.keys(currentKit);
		return {
			kit:
				keys.find((k) => /^kitsku$/i.test(k)) ||
				keys.find((k) => /^sku$/i.test(k)) ||
				keys.find((k) => /^kitid$/i.test(k)) ||
				'SKU',
			desc:
				keys.find((k) => /kitdesc/i.test(k)) ||
				keys.find((k) => /desc/i.test(k) && /kit/i.test(k)) ||
				keys.find((k) => /skudesc/i.test(k)) ||
				keys.find((k) => /desc/i.test(k)) ||
				keys.find((k) => /name/i.test(k)),
		};
	}, [currentKit]);

	const kitId = currentKit?.[fieldNames.kit] || '';
	const availQty = availInfo?.initialAvail ?? 0;
	const orderedQty = availInfo?.orderedQty ?? 0;
	const remainQty = availInfo?.remainQty ?? 0;
	const fmtNum = (n: number) => (n != null ? n.toLocaleString() : '0');

	// Build searchable list — include sort group value for section headers
	const kitOptions = useMemo(() => {
		return kitList.map((s, i) => {
			const id = s[fieldNames.kit] || s.SKU || s.KitSKU || '';
			const desc = fieldNames.desc ? s[fieldNames.desc] || '' : '';
			const label = desc ? `${id} — ${desc}` : id || `Kit ${i + 1}`;
			const groupVal = sortField ? String(s[sortField] || '(none)') : '';
			return { idx: i, id, label, groupVal };
		});
	}, [kitList, fieldNames, sortField]);

	const filteredOptions = useMemo(() => {
		if (!kitSearch) return kitOptions;
		const s = kitSearch.toLowerCase();
		return kitOptions.filter((o) => o.label.toLowerCase().includes(s) || o.groupVal.toLowerCase().includes(s));
	}, [kitOptions, kitSearch]);

	// Close dropdown on outside click
	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	useEffect(() => {
		if (dropOpen && searchRef.current) searchRef.current.focus();
	}, [dropOpen]);

	const selectKit = useCallback(
		(idx: number) => {
			onSelectIndex(idx);
			setDropOpen(false);
			setKitSearch('');
		},
		[onSelectIndex]
	);

	const currentLabel = kitOptions[kitIndex]?.label || '—';
	const currentGroup = kitOptions[kitIndex]?.groupVal || '';

	// Get display name for active sort field
	const sortDisplayName = useMemo(() => {
		if (!sortField) return '';
		return sortOptions.find((o) => o.columnName === sortField)?.displayName || sortField;
	}, [sortField, sortOptions]);

	return (
		<div className="obs-filter-box obs-kit-box">
			<div
				className="obs-filter-box__header"
				style={{ color: '#00695c' }}
				onClick={() => setIsOpen(!isOpen)}
			>
				<span className="obs-filter-box__header-left">
					<span className={`obs-filter-box__chevron ${isOpen ? 'obs-filter-box__chevron--open' : ''}`}>
						▶
					</span>
					Kit Selection
				</span>
				<span className="obs-filter-box__count">
					{kitList.length > 0 ? `${kitIndex + 1}/${kitList.length}` : '—'}
				</span>
			</div>

			{isOpen && (
				<div className="obs-filter-box__body">
					{/* Navigator */}
					<div className="obs-store-box__nav">
						<button
							className="obs-store-box__nav-btn"
							onClick={() => onNavigate(-1)}
							disabled={kitIndex <= 0 || loading}
						>
							‹
						</button>
						<div className="obs-store-box__nav-counter">
							{loading
								? 'Loading Kits...'
								: kitList.length > 0
									? `Kit ${kitIndex + 1} of ${kitList.length}`
									: 'No Kits'}
						</div>
						<button
							className="obs-store-box__nav-btn"
							onClick={() => onNavigate(1)}
							disabled={kitIndex >= kitList.length - 1 || loading}
						>
							›
						</button>
						<button
							className="obs-store-box__next-btn"
							onClick={() => onNavigate(1)}
							disabled={kitIndex >= kitList.length - 1 || loading}
						>
							Next Kit
						</button>
					</div>

					{/* Sort by attribute */}
					{sortOptions.length > 0 && (
						<div className="obs-store-box__sort">
							<label className="obs-store-box__sort-label">Sort by</label>
							<select
								className="obs-store-box__sort-select"
								value={sortField}
								onChange={(e) => onSortChange?.(e.target.value)}
							>
								<option value="">Default order</option>
								{sortOptions.map((opt) => (
									<option key={opt.columnName} value={opt.columnName}>
										{opt.displayName}
									</option>
								))}
							</select>
						</div>
					)}

					{/* Searchable Kit dropdown */}
					<div className="obs-ss" ref={dropRef}>
						<div className="obs-ss__trigger" onClick={() => setDropOpen(!dropOpen)}>
							<span className="obs-ss__val">{currentLabel}</span>
							{sortField && currentGroup && (
								<span className="obs-ss__sort-badge" title={`${sortDisplayName}: ${currentGroup}`}>
									{currentGroup}
								</span>
							)}
							<span className="obs-ss__arrow">▾</span>
						</div>
						{dropOpen && (
							<div className="obs-ss__drop">
								<input
									ref={searchRef}
									className="obs-ss__search"
									type="text"
									placeholder={
										sortField
											? 'Search kit, description, or group...'
											: 'Search kit or description...'
									}
									value={kitSearch}
									onChange={(e) => setKitSearch(e.target.value)}
									onClick={(e) => e.stopPropagation()}
								/>
								<div className="obs-ss__list" style={{ maxHeight: 260 }}>
									{filteredOptions.map((o, i) => {
										// Show group header when sort field is active and group changes
										const showGroupHeader =
											sortField &&
											!kitSearch &&
											(i === 0 || o.groupVal !== filteredOptions[i - 1]?.groupVal);
										return (
											<React.Fragment key={o.idx}>
												{showGroupHeader && (
													<div className="obs-ss__group-header">{o.groupVal}</div>
												)}
												<div
													className={`obs-ss__opt ${o.idx === kitIndex ? 'obs-ss__opt--sel' : ''}`}
													onClick={() => selectKit(o.idx)}
												>
													{o.label}
												</div>
											</React.Fragment>
										);
									})}
									{filteredOptions.length === 0 && (
										<div className="obs-ss__empty">No matching kits</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Kit Availability Metrics */}
					<div className="obs-store-box__metrics">
						<div className="obs-store-box__metric">
							<div className="obs-store-box__metric-label">Kit Avail</div>
							<div className="obs-store-box__metric-value">{fmtNum(availQty)}</div>
							<div className="obs-store-box__metric-unit">ea</div>
						</div>
						<div className="obs-store-box__metric">
							<div className="obs-store-box__metric-label">Kit Ord</div>
							<div className="obs-store-box__metric-value obs-store-box__metric-value--ordered">
								{fmtNum(orderedQty)}
							</div>
							<div className="obs-store-box__metric-unit">ea</div>
						</div>
						<div className="obs-store-box__metric">
							<div className="obs-store-box__metric-label">Kit Remain</div>
							<div
								className={`obs-store-box__metric-value ${remainQty < 0 ? 'obs-store-box__metric-value--negative' : ''}`}
							>
								{fmtNum(remainQty)}
							</div>
							<div className="obs-store-box__metric-unit">ea</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * Custom comparison — skip re-render when kit list content, index, and metrics haven't changed.
 */
function arePropsEqual(prev: KitSelectionBoxProps, next: KitSelectionBoxProps): boolean {
	if (prev.kitIndex !== next.kitIndex) return false;
	if (prev.loading !== next.loading) return false;
	if (prev.sortField !== next.sortField) return false;
	if (prev.onNavigate !== next.onNavigate) return false;
	if (prev.onSelectIndex !== next.onSelectIndex) return false;
	if (prev.onSortChange !== next.onSortChange) return false;

	// Check availability info
	if (prev.availInfo?.initialAvail !== next.availInfo?.initialAvail) return false;
	if (prev.availInfo?.orderedQty !== next.availInfo?.orderedQty) return false;
	if (prev.availInfo?.remainQty !== next.availInfo?.remainQty) return false;

	// Fast kitList comparison: length + first/last item reference
	const pl = prev.kitList,
		nl = next.kitList;
	if (pl.length !== nl.length) return false;
	if (pl.length > 0 && (pl[0] !== nl[0] || pl[pl.length - 1] !== nl[nl.length - 1])) return false;

	// sortOptions rarely changes — compare by length
	if ((prev.sortOptions?.length || 0) !== (next.sortOptions?.length || 0)) return false;

	return true;
}

export default memo(KitSelectionBox, arePropsEqual);
